import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight, Building2, ChevronDown, ChevronRight, ChevronUp, ClipboardList, Ghost, Home, IndianRupee, Loader2, MapPinned,
  Receipt, Replace, ScanSearch, Search, ShieldAlert, UserRoundCheck, UserRoundPlus, Users,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Collapsible, CollapsibleContent } from "./ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Skeleton } from "./ui/skeleton";
import { api, errMsg } from "../lib/api";
import { PaginationBar, TableRowsSkeleton, useDebounced } from "./PaginationBar";
import { StaffLabel, staffLabelText } from "./StaffAvatar";
import { PASSWORD_RULES_HINT } from "./ForgotPasswordDialog";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const n = (v) => Number(v || 0).toLocaleString("en-IN");
const plural = (count, one, many) => (Number(count) === 1 ? one : many ?? `${one}s`);
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? String(s).slice(0, 10) : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
const UNZONED = "UNZONED";
const zoneTitle = (p) => (!p?.zone_id || p?.zone_name === UNZONED ? "No zone" : /zone/i.test(p.zone_name || "") ? p.zone_name : `Zone ${p.zone_name}`);

/** Phase 163 — small amber tag that marks a placeholder collector wherever a staff row appears. */
export function PlaceholderBadge({ className = "", testId }) {
  return (
    <Badge variant="outline" className={`text-[10px] font-medium border-amber-300 bg-amber-50 text-amber-900 ${className}`}
      title="Placeholder collector — the zone's automatic stand-in for wards that have no active collector. Cannot sign in; its wards move to real collectors as they are mapped."
      data-testid={testId}>
      <Ghost className="h-3 w-3 mr-1" aria-hidden="true" /> Placeholder
    </Badge>
  );
}

const Stat = ({ icon: Icon, label, value, testId, tone = "" }) => (
  <span className={`text-xs text-muted-foreground flex items-center gap-1 tabular-nums whitespace-nowrap ${tone}`} title={label} data-testid={testId}>
    {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />} {value}
  </span>
);

const Chevron = ({ open, onClick, label, testId, size = "h-7 w-7" }) => (
  <Button type="button" variant="ghost" size="icon" className={`${size} shrink-0 text-muted-foreground hover:text-foreground`} onClick={onClick}
    aria-expanded={open} aria-label={label} data-testid={testId}>
    {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
  </Button>
);

// ------------------------------------------------------------------------------------------------ ward drill-down
/**
 * Phase 167 — what sits under one ward of a zone placeholder: summary strip + Properties / Collections / Collectors tabs
 * (server-paged, searchable). Mounted only while the ward row is expanded.
 */
export function PlaceholderWardDetail({ code, placeholderId, ward }) {
  const [tab, setTab] = useState("properties");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim(), 350);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => { setPage(1); }, [tab, q, limit]);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/t/${code}/placeholder-collectors/${placeholderId}/wards/${ward.id}`, { params: { tab, page, limit, search: q || undefined } })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(errMsg(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code, placeholderId, ward.id, tab, page, limit, q, nonce]);

  const s = data?.summary || ward;
  const items = data?.items || [];
  const paged = tab !== "collectors";
  const searchable = tab !== "collectors";
  const emptyText = { properties: "No properties are registered in this ward yet.", collections: "No receipts have been booked for this ward.", collectors: "Nobody has collected in this ward yet." }[tab];

  return (
    <div className="pl-10 pr-3 pb-3 pt-1 space-y-2" data-testid={`placeholder-ward-detail-${ward.id}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid={`placeholder-ward-summary-${ward.id}`}>
        <span className="text-muted-foreground">Properties <b className="text-foreground tabular-nums" data-testid={`ward-summary-properties-${ward.id}`}>{n(s.properties)}</b></span>
        <span className="text-muted-foreground">Receipts credited to placeholder <b className="text-foreground tabular-nums" data-testid={`ward-summary-receipts-${ward.id}`}>{n(s.receipts)}</b> · <b className="text-foreground tabular-nums" data-testid={`ward-summary-amount-${ward.id}`}>{inr(s.amount)}</b></span>
        <span className="text-muted-foreground">Assigned <b className="text-foreground tabular-nums">{n(s.assignments)}</b></span>
        {s.uncredited > 0 && <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-900" data-testid={`ward-summary-uncredited-${ward.id}`}>{n(s.uncredited)} uncredited — next scan attributes</Badge>}
        {data?.ward?.mohallas != null && <span className="text-muted-foreground">{n(data.ward.mohallas)} {plural(data.ward.mohallas, "mohalla")}</span>}
        {data?.real_collectors?.length > 0 && (
          <span className="text-[hsl(var(--success))] flex items-center gap-1" data-testid={`ward-summary-covered-${ward.id}`}>
            <UserRoundCheck className="h-3 w-3" aria-hidden="true" /> Now covered by {data.real_collectors.map((c) => c.name).join(", ")} — the next scan moves this ward's data to them
          </span>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-wrap items-center gap-2">
          <TabsList className="h-8">
            <TabsTrigger value="properties" className="text-xs h-7" data-testid={`ward-tab-properties-${ward.id}`}><Home className="h-3 w-3 mr-1" aria-hidden="true" /> Properties</TabsTrigger>
            <TabsTrigger value="collections" className="text-xs h-7" data-testid={`ward-tab-collections-${ward.id}`}><Receipt className="h-3 w-3 mr-1" aria-hidden="true" /> Collections</TabsTrigger>
            <TabsTrigger value="collectors" className="text-xs h-7" data-testid={`ward-tab-collectors-${ward.id}`}><Users className="h-3 w-3 mr-1" aria-hidden="true" /> Collectors</TabsTrigger>
          </TabsList>
          {searchable && (
            <div className="relative ml-auto w-full sm:w-64">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 pl-8 text-xs"
                placeholder={tab === "properties" ? "Search PIN, owner, house no…" : "Search receipt no, PIN, collector…"} aria-label="Search within this ward"
                data-testid={`ward-search-${ward.id}`} />
            </div>
          )}
        </div>

        {error ? (
          <div className="mt-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive flex items-center justify-between gap-2" data-testid={`ward-detail-error-${ward.id}`}>
            <span>Could not load this ward: {error}</span>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setNonce((k) => k + 1)} data-testid={`ward-detail-retry-${ward.id}`}>Retry</Button>
          </div>
        ) : (
          <div className="mt-2 rounded-md border bg-background overflow-hidden">
            {loading && !data ? <TableRowsSkeleton rows={3} cols={5} testId={`ward-detail-skeleton-${ward.id}`} /> : items.length === 0 ? (
              <p className="px-3 py-4 text-xs text-muted-foreground text-center" data-testid={`ward-detail-empty-${ward.id}`}>{q ? `Nothing matches “${q}”.` : emptyText}</p>
            ) : (
              <>
                <TabsContent value="properties" className="m-0">
                  <Table className="text-xs" data-testid={`ward-properties-table-${ward.id}`}>
                    <TableHeader><TableRow className="hover:bg-transparent">
                      <TableHead className="h-8 text-[11px]">PIN</TableHead><TableHead className="h-8 text-[11px]">Owner</TableHead>
                      <TableHead className="h-8 text-[11px] hidden md:table-cell">Mohalla / address</TableHead>
                      <TableHead className="h-8 text-[11px] text-right">Receipts</TableHead><TableHead className="h-8 text-[11px] text-right">Paid</TableHead>
                      <TableHead className="h-8 text-[11px] hidden sm:table-cell">Last payment</TableHead><TableHead className="h-8 text-[11px]">Assigned to</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {items.map((r) => (
                        <TableRow key={r.id} data-testid={`ward-property-row-${r.pin}`}>
                          <TableCell className="py-1.5 font-mono font-medium">{r.pin}</TableCell>
                          <TableCell className="py-1.5">{r.owner_name || "—"}{r.mobile && <span className="block text-[10px] text-muted-foreground">{r.mobile}</span>}</TableCell>
                          <TableCell className="py-1.5 hidden md:table-cell text-muted-foreground">{r.mohalla_name || "—"}{r.address && <span className="block text-[10px] truncate max-w-[260px]">{r.house_no ? `${r.house_no}, ` : ""}{r.address}</span>}</TableCell>
                          <TableCell className="py-1.5 text-right tabular-nums">{n(r.receipts)}</TableCell>
                          <TableCell className="py-1.5 text-right tabular-nums font-medium">{inr(r.paid_amount)}</TableCell>
                          <TableCell className="py-1.5 hidden sm:table-cell text-muted-foreground">{fmtDate(r.last_payment_date)}</TableCell>
                          <TableCell className="py-1.5">{r.assigned_to_placeholder ? <PlaceholderBadge testId={`ward-property-assigned-${r.pin}`} /> : <span className="text-muted-foreground">—</span>}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TabsContent>
                <TabsContent value="collections" className="m-0">
                  {data?.totals && (
                    <p className="px-3 py-1.5 text-[11px] text-muted-foreground border-b bg-muted/30 tabular-nums" data-testid={`ward-collections-totals-${ward.id}`}>
                      {n(data.totals.receipts)} {plural(data.totals.receipts, "receipt")} · {inr(data.totals.amount)} booked in this ward — <b className="text-foreground">{n(data.totals.placeholder_receipts)}</b> ({inr(data.totals.placeholder_amount)}) credited to {data.placeholder?.name}
                      {data.totals.uncredited > 0 && <>, <b className="text-amber-800">{n(data.totals.uncredited)} uncredited</b></>}
                    </p>
                  )}
                  <Table className="text-xs" data-testid={`ward-collections-table-${ward.id}`}>
                    <TableHeader><TableRow className="hover:bg-transparent">
                      <TableHead className="h-8 text-[11px]">Receipt</TableHead><TableHead className="h-8 text-[11px]">Date</TableHead>
                      <TableHead className="h-8 text-[11px]">Property</TableHead><TableHead className="h-8 text-[11px] hidden sm:table-cell">Mode</TableHead>
                      <TableHead className="h-8 text-[11px]">Collected by</TableHead><TableHead className="h-8 text-[11px] text-right">Amount</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {items.map((r) => (
                        <TableRow key={r.id} data-testid={`ward-collection-row-${r.receipt_number || r.id}`}>
                          <TableCell className="py-1.5 font-mono">{r.receipt_number || "—"}</TableCell>
                          <TableCell className="py-1.5 text-muted-foreground whitespace-nowrap">{fmtDate(r.payment_date)}</TableCell>
                          <TableCell className="py-1.5"><span className="font-mono">{r.pin || "—"}</span>{r.owner_name && <span className="block text-[10px] text-muted-foreground">{r.owner_name}</span>}</TableCell>
                          <TableCell className="py-1.5 hidden sm:table-cell text-muted-foreground">{r.payment_mode || "—"}{r.collection_channel && <span className="block text-[10px]">{r.collection_channel}</span>}</TableCell>
                          <TableCell className="py-1.5">
                            {r.credited_to_placeholder ? <span className="inline-flex items-center gap-1"><Ghost className="h-3 w-3 text-amber-700" aria-hidden="true" /> {r.collected_by_name}</span>
                              : r.collected_by ? <StaffLabel code={code} userId={r.collected_by} name={r.collected_by_name} /> : <span className="text-muted-foreground">Not recorded</span>}
                          </TableCell>
                          <TableCell className="py-1.5 text-right tabular-nums font-medium">{inr(r.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TabsContent>
                <TabsContent value="collectors" className="m-0">
                  <Table className="text-xs" data-testid={`ward-collectors-table-${ward.id}`}>
                    <TableHeader><TableRow className="hover:bg-transparent">
                      <TableHead className="h-8 text-[11px]">Collector</TableHead><TableHead className="h-8 text-[11px] hidden sm:table-cell">Status</TableHead>
                      <TableHead className="h-8 text-[11px] text-right">Receipts</TableHead><TableHead className="h-8 text-[11px] text-right">Amount</TableHead>
                      <TableHead className="h-8 text-[11px] hidden md:table-cell">Period</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {items.map((r, i) => (
                        <TableRow key={r.id || `none-${i}`} data-testid={`ward-collector-row-${r.id || "none"}`}>
                          <TableCell className="py-1.5 font-medium">
                            {r.is_placeholder ? <span className="inline-flex items-center gap-1"><Ghost className="h-3 w-3 text-amber-700" aria-hidden="true" /> {r.name}</span>
                              : r.id && r.known ? <StaffLabel code={code} userId={r.id} name={r.name} empCode={r.employee_id} /> : <span className="text-muted-foreground">{r.name}</span>}
                          </TableCell>
                          <TableCell className="py-1.5 hidden sm:table-cell">
                            {r.is_placeholder ? <PlaceholderBadge /> : !r.id ? <Badge variant="outline" className="text-[10px]">No collector recorded</Badge>
                              : !r.known ? <Badge variant="outline" className="text-[10px]">Former staff</Badge>
                              : r.is_active === false ? <Badge variant="outline" className="text-[10px]">Inactive</Badge>
                              : <Badge variant="outline" className="text-[10px] bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20">Active {r.role === "supervisor" ? "supervisor" : "collector"}</Badge>}
                          </TableCell>
                          <TableCell className="py-1.5 text-right tabular-nums">{n(r.receipts)}</TableCell>
                          <TableCell className="py-1.5 text-right tabular-nums font-medium">{inr(r.amount)}</TableCell>
                          <TableCell className="py-1.5 hidden md:table-cell text-muted-foreground whitespace-nowrap">{fmtDate(r.first)} – {fmtDate(r.last)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TabsContent>
              </>
            )}
            {paged && data && data.total > 0 && (
              <div className="px-3 pb-2">
                <PaginationBar page={page} limit={limit} total={data.total} loading={loading} onPageChange={setPage} onLimitChange={setLimit}
                  sizes={[10, 25, 50]} testId={`ward-pagination-${ward.id}`} className="pt-2" />
              </div>
            )}
          </div>
        )}
      </Tabs>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ assign shortcut
/**
 * Phase 167 — "Assign real collector" from a ward row: pick the collector, then the existing Territory dialog opens with
 * the ward already ticked. props: code, target {ward, placeholder}, users, open, onOpenChange, onPick(collector, ward)
 */
export function AssignWardCollectorDialog({ target, users = [], open, onOpenChange, onPick }) {
  const [collectorId, setCollectorId] = useState("");
  const zoneId = target?.placeholder?.zone_id || null;
  const supervisorsById = useMemo(() => Object.fromEntries(users.filter((u) => u.role === "supervisor").map((u) => [u.id, u])), [users]);
  // a collector can take the ward when they have no supervisor, or their supervisor's territory includes the ward's zone
  const coversZone = (c) => {
    if (!c.reports_to_id) return true;
    const s = supervisorsById[c.reports_to_id];
    if (!s) return true;
    const zones = s.zone_ids || [];
    return zoneId ? zones.includes(zoneId) : zones.length === 0;
  };
  const collectors = useMemo(() => users.filter((u) => u.role === "tax_collector" && !u.is_placeholder && u.is_active !== false && !u.parent_user_id)
    .map((c) => ({ ...c, eligible: coversZone(c) }))
    .sort((a, b) => (a.eligible === b.eligible ? (a.name || "").localeCompare(b.name || "") : a.eligible ? -1 : 1)), [users, zoneId]); // eslint-disable-line
  useEffect(() => { if (open) setCollectorId(""); }, [open, target]);
  if (!target) return null;
  const { ward, placeholder } = target;
  const chosen = collectors.find((c) => c.id === collectorId);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="assign-ward-collector-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><UserRoundPlus className="h-5 w-5 text-primary" aria-hidden="true" /> Assign a real collector to {ward.name}</DialogTitle>
          <DialogDescription>
            {ward.name} currently sits under <span className="font-medium text-foreground">{placeholder.name}</span>
            {ward.receipts > 0 && <> with <span className="font-medium text-foreground tabular-nums">{n(ward.receipts)}</span> {plural(ward.receipts, "receipt")} ({inr(ward.amount)})</>}.
            Pick who takes the ward over — their territory opens with {ward.name} ticked; once you save there, every receipt, printed receipt,
            visit and property assignment of this ward moves from the placeholder to them automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label className="text-xs">Tax collector</Label>
          <Select value={collectorId} onValueChange={setCollectorId}>
            <SelectTrigger className="h-10" data-testid="assign-ward-collector-select"><SelectValue placeholder="Select a tax collector" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {collectors.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`assign-ward-collector-option-${c.id}`} data-eligible={c.eligible}>
                  {staffLabelText(c.name, c.employee_id)}{c.ward_names?.length ? ` — ${c.ward_names.length} ${plural(c.ward_names.length, "ward")}` : " — no territory yet"}
                  {!c.eligible ? " · supervisor does not cover this zone" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {collectors.length === 0 && <p className="text-xs text-amber-700">No active tax collectors yet — create one first, or convert the placeholder into the real person.</p>}
          {chosen && !chosen.eligible && (
            <p className="text-xs text-amber-700" data-testid="assign-ward-ineligible-hint">
              {chosen.name} reports to {supervisorsById[chosen.reports_to_id]?.name || "a supervisor"} whose territory does not include {zoneTitle(placeholder).toLowerCase() === "no zone" ? "unzoned wards" : zoneTitle(placeholder)} — the ward will not be selectable until that supervisor's territory is widened.
            </p>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="assign-ward-cancel-button">Cancel</Button>
          <Button disabled={!chosen} onClick={() => { onOpenChange(false); onPick?.(chosen, ward); }} data-testid="assign-ward-open-territory-button">
            <MapPinned className="h-4 w-4 mr-1.5" aria-hidden="true" /> Open {chosen ? `${chosen.name}'s` : "the"} territory
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------------------------------------ panel
/**
 * Phase 167 — card above the staff directory: one placeholder per ZONE (GESIL-<ZONE>) holding every ward of the zone that
 * has no active real collector. Each placeholder is an expandable row → its wards → inside each ward the properties,
 * collection data and the people who collected there. "Scan now" rebalances (dry run first).
 * props: code, readOnly, refreshKey, users (for the assign shortcut), onReplace(placeholder), onAssignCollector(collector, ward)
 */
export function PlaceholderCollectorsPanel({ code, readOnly = false, refreshKey = 0, users = [], onReplace, onAssignCollector }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [plan, setPlan] = useState(null);     // dry-run result awaiting confirmation
  const [openZones, setOpenZones] = useState(() => new Set());
  const [openWards, setOpenWards] = useState(() => new Set());
  const [assignTarget, setAssignTarget] = useState(null);

  const load = () => api.get(`/t/${code}/placeholder-collectors`).then((r) => { setData(r.data); setError(null); })
    .catch((e) => setError(errMsg(e)));
  useEffect(() => {
    load();
    // territory / staff changes rebalance in the background a moment later — pick up the result too
    const t = refreshKey ? setTimeout(load, 3500) : null;
    return () => { if (t) clearTimeout(t); };
  }, [code, refreshKey]); // eslint-disable-line

  const toggleIn = (setter) => (id) => setter((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const toggleZone = toggleIn(setOpenZones);
  const toggleWard = toggleIn(setOpenWards);

  const scan = async (apply) => {
    setScanning(true);
    try {
      const r = await api.post(`/t/${code}/placeholder-collectors/scan`, null, { params: { dry_run: !apply } });
      const d = r.data;
      if (!apply) {
        const changes = (d.to_create || 0) + (d.to_retire || 0) + (d.joining || 0) + (d.leaving || 0);
        setPlan({ ...d, changes });
      } else {
        setPlan(null);
        const bits = [];
        if (d.created?.length) bits.push(`${d.created.length} ${plural(d.created.length, "placeholder")} created`);
        if (d.folded?.length) bits.push(`${d.folded.length} legacy ${plural(d.folded.length, "placeholder")} folded`);
        if (d.migrated?.length) bits.push(`${d.migrated.length} ${plural(d.migrated.length, "ward")} migrated to real collectors`);
        if (d.retired?.length) bits.push(`${d.retired.length} retired`);
        if (d.credited) bits.push(`${n(d.credited)} ${plural(d.credited, "receipt")} (${inr(d.credited_amount)}) attributed`);
        toast.success(bits.length ? "Scan complete" : "Scan complete — everything already in balance", {
          description: bits.length ? bits.join(" · ") : `${n(d.unmapped_wards)} ${plural(d.unmapped_wards, "ward")} without a collector ${d.unmapped_wards === 1 ? "sits" : "sit"} under ${d.prefix} placeholders.`,
        });
        load();
        setOpen(true);
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setScanning(false);
    }
  };

  if (error) {
    return (
      <Card className="shadow-sm mb-3 border-destructive/40" data-testid="placeholder-panel-error">
        <CardContent className="p-3 text-xs text-destructive flex items-center justify-between gap-2">
          <span>Could not load placeholder collectors: {error}</span>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={load} data-testid="placeholder-panel-retry">Retry</Button>
        </CardContent>
      </Card>
    );
  }
  if (!data) {
    return <Card className="shadow-sm mb-3" data-testid="placeholder-panel-loading"><CardContent className="p-3"><Skeleton className="h-5 w-72" /></CardContent></Card>;
  }
  const items = data.items || [];
  const planZones = (plan?.zones || []).filter((z) => z.action === "create" || z.action === "retire" || z.joining?.length || z.leaving?.length);

  return (
    <Card className="shadow-sm mb-3 border-amber-200/80" data-testid="placeholder-panel" data-count={data.total}>
      <CardContent className="p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="h-8 w-8 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center shrink-0"><Ghost className="h-4 w-4" aria-hidden="true" /></span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium flex flex-wrap items-center gap-x-2 gap-y-1">
              Placeholder collectors
              <Badge variant="secondary" className="tabular-nums" data-testid="placeholder-panel-total">{n(data.total)}</Badge>
              {data.wards > 0 && <Badge variant="outline" className="tabular-nums font-normal" data-testid="placeholder-panel-wards">{n(data.wards)} {plural(data.wards, "ward")} without a collector</Badge>}
              {data.replace_ready > 0 && (
                <Badge className="bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-0 tabular-nums" data-testid="placeholder-panel-ready">
                  <UserRoundCheck className="h-3 w-3 mr-1" aria-hidden="true" /> {n(data.replace_ready)} {plural(data.replace_ready, "has", "have")} wards with a real collector
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {data.total === 0
                ? `Every ward has an active collector. Zones with wards nobody covers get one stand-in each (“${data.prefix}-<ZONE>”, “${data.prefix}-UNZONED” for wards without a zone) so their receipts show up in Collector Targets and reports.`
                : <>One stand-in per zone holds the wards with no active collector and owns <span className="font-medium tabular-nums text-foreground" data-testid="placeholder-panel-receipts">{n(data.receipts)}</span> {plural(data.receipts, "receipt")} worth <span className="font-medium tabular-nums text-foreground" data-testid="placeholder-panel-amount">{inr(data.amount)}</span>. Map a real collector to a ward and its data moves to them automatically; expand a row to see what each ward holds.</>}
            </p>
          </div>
          {!readOnly && (
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => scan(false)} disabled={scanning} data-testid="placeholder-scan-button">
              {scanning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ScanSearch className="h-3.5 w-3.5 mr-1.5" />} Scan now
            </Button>
          )}
          {items.length > 0 && (
            <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="placeholder-panel-toggle">
              {open ? <><ChevronUp className="h-3.5 w-3.5 mr-1" /> Hide</> : <><ChevronDown className="h-3.5 w-3.5 mr-1" /> Show list</>}
            </Button>
          )}
        </div>

        {plan && (
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950" role="status" data-testid="placeholder-scan-plan">
            {plan.changes === 0 ? (
              <p className="font-medium flex items-center gap-1.5" data-testid="placeholder-scan-plan-noop">
                <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" /> Everything is in balance — {n(plan.unmapped_wards)} {plural(plan.unmapped_wards, "ward")} without a collector already {plan.unmapped_wards === 1 ? "sits" : "sit"} under {plan.zones?.filter((z) => z.placeholder).length || 0} zone {plural(plan.zones?.filter((z) => z.placeholder).length || 0, "placeholder")}. Running the scan again only re-attributes receipts that still have no collector.
              </p>
            ) : (
              <>
                <p className="font-medium flex items-center gap-1.5 flex-wrap" data-testid="placeholder-scan-plan-summary">
                  <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" />
                  {plan.to_create > 0 && <span>{plan.to_create} {plural(plan.to_create, "placeholder")} to create</span>}
                  {plan.joining > 0 && <span>· {plan.joining} {plural(plan.joining, "ward")} joining a placeholder</span>}
                  {plan.leaving > 0 && <span>· {plan.leaving} {plural(plan.leaving, "ward")} moving to real collectors (data migrates)</span>}
                  {plan.to_retire > 0 && <span>· {plan.to_retire} {plural(plan.to_retire, "placeholder")} to retire</span>}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {planZones.slice(0, 8).map((z) => (
                    <li key={z.zone_key} className="flex flex-wrap items-center gap-1.5" data-testid={`placeholder-scan-zone-${z.zone_key}`}>
                      <span className="font-medium">{z.placeholder_name}</span>
                      <span className="text-muted-foreground">· {z.zone_name === UNZONED ? "no zone" : z.zone_name} · {z.unmapped.length} of {z.wards_total} {plural(z.wards_total, "ward")} without a collector</span>
                      {z.action === "create" && <Badge variant="outline" className="text-[10px] bg-white">new placeholder</Badge>}
                      {z.action === "retire" && <Badge variant="outline" className="text-[10px] bg-white">retire</Badge>}
                      {z.joining?.length > 0 && <Badge variant="outline" className="text-[10px] bg-white">+ {z.joining.map((w) => w.name).join(", ")}</Badge>}
                      {z.leaving?.length > 0 && <Badge variant="outline" className="text-[10px] bg-white text-[hsl(var(--success))]">→ real collector: {z.leaving.map((w) => w.name || "ward").join(", ")}</Badge>}
                    </li>
                  ))}
                  {planZones.length > 8 && <li className="text-muted-foreground">+{planZones.length - 8} more zones</li>}
                </ul>
              </>
            )}
            <div className="mt-2 flex gap-2">
              <Button size="sm" className="h-7 text-xs" onClick={() => scan(true)} disabled={scanning} data-testid="placeholder-scan-apply">
                {scanning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Ghost className="h-3.5 w-3.5 mr-1.5" />} {plan.changes === 0 ? "Run scan anyway" : "Apply — rebalance placeholders"}
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setPlan(null)} data-testid="placeholder-scan-cancel">Cancel</Button>
            </div>
          </div>
        )}

        {open && items.length > 0 && (
          <ul className="mt-3 divide-y rounded-md border bg-background" data-testid="placeholder-list">
            {items.map((p) => {
              const zOpen = openZones.has(p.id);
              return (
                <Collapsible key={p.id} asChild open={zOpen} onOpenChange={() => toggleZone(p.id)}>
                  <li data-testid={`placeholder-item-${p.name}`} data-expanded={zOpen}>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-1.5 pr-3 py-2 text-sm">
                      <Chevron open={zOpen} onClick={() => toggleZone(p.id)} label={`${zOpen ? "Collapse" : "Expand"} ${p.name}`} testId={`placeholder-toggle-${p.name}`} />
                      <button type="button" className="font-medium flex items-center gap-1.5 text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => toggleZone(p.id)} data-testid={`placeholder-name-${p.name}`}>
                        <Ghost className="h-3.5 w-3.5 text-amber-700" aria-hidden="true" /> {p.name}
                      </button>
                      <Badge variant="outline" className="text-[10px] font-normal" data-testid={`placeholder-zone-${p.name}`}>{zoneTitle(p)}</Badge>
                      <Stat icon={Building2} label="Wards without a collector / wards in the zone" testId={`placeholder-wards-${p.name}`}
                        value={`${n(p.ward_ids?.length || 0)} of ${n(p.zone_wards_total)} ${plural(p.zone_wards_total, "ward")}`} />
                      <Stat icon={Home} label="Properties in those wards" value={`${n(p.properties)} ${plural(p.properties, "property", "properties")}`} testId={`placeholder-properties-${p.name}`} />
                      <Stat icon={Receipt} label="Receipts credited to this placeholder" value={`${n(p.receipts)} ${plural(p.receipts, "receipt")}`} testId={`placeholder-receipts-${p.name}`} />
                      <Stat icon={IndianRupee} label="Amount credited" value={inr(p.amount)} testId={`placeholder-amount-${p.name}`} />
                      {p.reports_to_name && <Stat icon={ClipboardList} label="Reports to" value={`Reports to ${p.reports_to_name}`} />}
                      {p.covered_by?.length > 0 && (
                        <span className="text-xs text-[hsl(var(--success))] flex items-center gap-1" data-testid={`placeholder-covered-${p.name}`}>
                          <UserRoundCheck className="h-3 w-3" aria-hidden="true" /> {p.covered_by.map((c) => c.name).join(", ")} now cover{p.covered_by.length === 1 ? "s" : ""} {p.covered_by.reduce((s, c) => s + (c.wards?.length || 0), 0)} {plural(p.covered_by.reduce((s, c) => s + (c.wards?.length || 0), 0), "ward")} here — scan to migrate
                        </span>
                      )}
                      {!readOnly && (
                        <Button size="sm" variant={p.replace_ready ? "default" : "outline"} className="h-7 text-xs ml-auto" onClick={() => onReplace?.(p)}
                          data-testid={`placeholder-replace-${p.name}`}>
                          <Replace className="h-3.5 w-3.5 mr-1" /> Replace with real collector
                        </Button>
                      )}
                    </div>
                    <CollapsibleContent>
                      <ul className="border-t bg-muted/20 divide-y" data-testid={`placeholder-ward-list-${p.name}`}>
                        {(p.wards || []).length === 0 && (
                          <li className="pl-12 pr-3 py-2 text-xs text-muted-foreground" data-testid={`placeholder-ward-list-empty-${p.name}`}>
                            No wards mapped — every ward of this zone has a real collector. The entry is removed on the next scan once nothing refers to it.
                          </li>
                        )}
                        {(p.wards || []).map((w) => {
                          const wOpen = openWards.has(w.id);
                          return (
                            <Collapsible key={w.id} asChild open={wOpen} onOpenChange={() => toggleWard(w.id)}>
                              <li data-testid={`placeholder-ward-${w.id}`} data-ward-name={w.name} data-expanded={wOpen}>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-6 pr-3 py-1.5">
                                  <Chevron open={wOpen} onClick={() => toggleWard(w.id)} label={`${wOpen ? "Collapse" : "Expand"} ${w.name}`} testId={`placeholder-ward-toggle-${w.id}`} size="h-6 w-6" />
                                  <button type="button" className="text-sm font-medium flex items-center gap-1.5 text-left rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    onClick={() => toggleWard(w.id)} data-testid={`placeholder-ward-name-${w.id}`}>
                                    <MapPinned className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" /> {w.name || "Unnamed ward"}
                                  </button>
                                  <Stat icon={Home} label="Properties" value={`${n(w.properties)} ${plural(w.properties, "property", "properties")}`} testId={`placeholder-ward-properties-${w.id}`} />
                                  <Stat icon={Receipt} label="Receipts credited to the placeholder" value={`${n(w.receipts)} ${plural(w.receipts, "receipt")} · ${inr(w.amount)}`} testId={`placeholder-ward-receipts-${w.id}`} />
                                  <Stat label="Property assignments held by the placeholder" value={`${n(w.assignments)} assigned`} />
                                  {w.uncredited > 0 && <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-900" data-testid={`placeholder-ward-uncredited-${w.id}`}>{n(w.uncredited)} uncredited</Badge>}
                                  {!readOnly && (
                                    <Button size="sm" variant="outline" className="h-6 text-[11px] px-2 ml-auto" onClick={() => setAssignTarget({ ward: w, placeholder: p })}
                                      data-testid={`placeholder-ward-assign-${w.id}`}>
                                      <UserRoundPlus className="h-3 w-3 mr-1" aria-hidden="true" /> Assign real collector
                                    </Button>
                                  )}
                                </div>
                                <CollapsibleContent>
                                  {wOpen && <PlaceholderWardDetail code={code} placeholderId={p.id} ward={w} />}
                                </CollapsibleContent>
                              </li>
                            </Collapsible>
                          );
                        })}
                      </ul>
                    </CollapsibleContent>
                  </li>
                </Collapsible>
              );
            })}
          </ul>
        )}
      </CardContent>
      <AssignWardCollectorDialog target={assignTarget} users={users} open={!!assignTarget} onOpenChange={(v) => !v && setAssignTarget(null)}
        onPick={(collector, ward) => onAssignCollector?.(collector, ward)} />
    </Card>
  );
}

// ------------------------------------------------------------------------------------------------ replace dialog
/**
 * Phase 163/167 — replace a placeholder: merge everything into an existing collector, or convert the record into the
 * real person. props: code, placeholder (list item or user row), users (users array), open, onOpenChange, onDone
 */
export function ReplacePlaceholderDialog({ code, placeholder: placeholderProp, users = [], open, onOpenChange, onDone }) {
  const [tab, setTab] = useState("merge");
  const [targetId, setTargetId] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", employee_id: "", designation: "Tax Collector", reports_to_id: "", password: "" });
  // when opened from a plain staff row (no receipts / covered_by yet) enrich it from the placeholder list
  const [detail, setDetail] = useState(null);
  const placeholder = useMemo(() => (detail && placeholderProp && detail.id === placeholderProp.id ? { ...placeholderProp, ...detail } : placeholderProp), [detail, placeholderProp]);

  const collectors = useMemo(() => users.filter((u) => u.role === "tax_collector" && !u.is_placeholder && u.is_active !== false && !u.parent_user_id)
    .sort((a, b) => (a.name || "").localeCompare(b.name || "")), [users]);
  const supervisors = useMemo(() => users.filter((u) => u.role === "supervisor" && u.is_active !== false && !u.parent_user_id)
    .sort((a, b) => (a.name || "").localeCompare(b.name || "")), [users]);
  const suggested = new Set((placeholder?.covered_by || []).map((c) => c.id));

  useEffect(() => {
    if (!open || !placeholderProp || placeholderProp.receipts != null) { setDetail(null); return; }
    let alive = true;
    api.get(`/t/${code}/placeholder-collectors`)
      .then((r) => { if (alive) setDetail((r.data.items || []).find((i) => i.id === placeholderProp.id) || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [open, placeholderProp, code]);

  useEffect(() => {
    if (open) {
      setTab("merge");
      setTargetId(placeholderProp?.covered_by?.[0]?.id || "");
      setPreview(null);
      setConfirm("");
      setForm({ name: "", email: "", mobile: "", employee_id: "", designation: "Tax Collector", reports_to_id: placeholderProp?.reports_to_id || "", password: "" });
    }
  }, [open, placeholderProp]);
  // once the enriched detail arrives, pre-select the real collector that now covers a ward (if none chosen yet)
  useEffect(() => {
    if (open && detail?.covered_by?.length) setTargetId((t) => t || detail.covered_by[0].id);
  }, [open, detail]);

  useEffect(() => {
    if (!open || !placeholderProp || !targetId) { setPreview(null); return; }
    let alive = true;
    setPreviewBusy(true);
    api.get(`/t/${code}/placeholder-collectors/${placeholderProp.id}/merge-preview`, { params: { target_user_id: targetId } })
      .then((r) => { if (alive) setPreview(r.data); })
      .catch((e) => { if (alive) { setPreview(null); toast.error(errMsg(e)); } })
      .finally(() => { if (alive) setPreviewBusy(false); });
    return () => { alive = false; };
  }, [open, placeholderProp, targetId, code]);

  if (!placeholder) return null;
  const target = collectors.find((c) => c.id === targetId);
  const wardCount = placeholder.ward_names?.length || placeholder.ward_ids?.length || 0;
  const standsFor = wardCount
    ? `${wardCount} ${plural(wardCount, "ward")} of ${zoneTitle(placeholder).toLowerCase() === "no zone" ? "wards without a zone" : zoneTitle(placeholder)} (${(placeholder.ward_names || []).filter(Boolean).slice(0, 6).join(", ")}${wardCount > 6 ? ` +${wardCount - 6}` : ""})`
    : placeholder.placeholder_zone_name ? `${zoneTitle({ zone_id: true, zone_name: placeholder.placeholder_zone_name })} (no wards left)` : "its zone";

  const merge = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/placeholder-collectors/${placeholder.id}/merge-into`, { target_user_id: targetId, confirm });
      const d = r.data;
      toast.success(`${placeholder.name} replaced by ${d.target.name}`, {
        description: `${n(d.receipts)} ${plural(d.receipts, "receipt")} (${inr(d.amount)}), ${n(d.assignments)} ${plural(d.assignments, "property", "properties")} and ${n(d.targets)} ${plural(d.targets, "target")} transferred`,
      });
      onOpenChange(false);
      onDone?.(d);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const convert = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/placeholder-collectors/${placeholder.id}/convert`, { ...form, reports_to_id: form.reports_to_id || null });
      toast.success(`${placeholder.name} is now ${r.data.user?.name}`, { description: "Every receipt, assignment and target stays with this record. They will set a new password at first sign-in." });
      onOpenChange(false);
      onDone?.(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="replace-placeholder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Replace className="h-5 w-5 text-primary" /> Replace {placeholder.name} with the real collector</DialogTitle>
          <DialogDescription>
            {placeholder.name} stands in for {standsFor}
            {placeholder.receipts != null && <> and currently owns <span className="font-medium text-foreground tabular-nums">{n(placeholder.receipts)}</span> {plural(placeholder.receipts, "receipt")} ({inr(placeholder.amount)})</>}.
            Choose how the real staff member takes over all of it. To hand over a single ward instead, map a collector to that ward — it migrates on its own.
          </DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="merge" data-testid="replace-tab-merge">Merge into existing collector</TabsTrigger>
            <TabsTrigger value="convert" data-testid="replace-tab-convert">Convert to real staff</TabsTrigger>
          </TabsList>

          <TabsContent value="merge" className="space-y-3 mt-3">
            <div className="space-y-1">
              <Label className="text-xs">Real collector who takes over</Label>
              <Select value={targetId} onValueChange={(v) => { setTargetId(v); setConfirm(""); }}>
                <SelectTrigger className="h-10" data-testid="replace-target-select"><SelectValue placeholder="Select a tax collector" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {collectors.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`replace-target-option-${c.id}`}>
                      {staffLabelText(c.name, c.employee_id)}{suggested.has(c.id) ? " — already covers a ward here" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {collectors.length === 0 && <p className="text-xs text-amber-700">No active tax collectors yet — create one first, or use “Convert to real staff”.</p>}
            </div>
            {targetId && (
              <div className="rounded-md border bg-muted/30 p-3 text-sm" data-testid="replace-merge-preview">
                {previewBusy && !preview ? <Skeleton className="h-16 w-full" /> : preview ? (
                  <>
                    <p className="font-medium flex items-center gap-1.5 flex-wrap">
                      <Ghost className="h-4 w-4 text-amber-700" aria-hidden="true" /> {preview.placeholder.name}
                      <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                      <StaffLabel code={code} userId={preview.target.id} name={preview.target.name} empCode={preview.target.employee_id} />
                    </p>
                    <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <li className="flex justify-between"><span className="text-muted-foreground">Receipts</span><span className="tabular-nums font-medium" data-testid="replace-preview-receipts">{n(preview.receipts)} · {inr(preview.amount)}</span></li>
                      <li className="flex justify-between"><span className="text-muted-foreground">Property assignments</span><span className="tabular-nums font-medium" data-testid="replace-preview-assignments">{n(preview.assignments)}</span></li>
                      <li className="flex justify-between"><span className="text-muted-foreground">Collection visits</span><span className="tabular-nums font-medium">{n(preview.visits)}</span></li>
                      <li className="flex justify-between"><span className="text-muted-foreground">Monthly targets</span><span className="tabular-nums font-medium">{n(preview.targets)}{preview.target_overlaps?.length ? ` (${preview.target_overlaps.length} added to existing)` : ""}</span></li>
                    </ul>
                    {preview.wards_added?.length > 0 && (
                      <p className="mt-2 text-xs text-muted-foreground" data-testid="replace-preview-wards">
                        {target?.name} will also be mapped to: {preview.wards_added.map((w) => w.name).join(", ")}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-muted-foreground">{preview.placeholder.name} is removed afterwards. This cannot be undone.</p>
                  </>
                ) : <p className="text-xs text-muted-foreground">Preview unavailable.</p>}
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">Type <span className="font-mono font-semibold">MERGE</span> to confirm</Label>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="MERGE" className="h-9 font-mono" disabled={!targetId} data-testid="replace-merge-confirm-input" />
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="replace-cancel-button">Cancel</Button>
              <Button onClick={merge} disabled={!targetId || !preview || confirm.trim().toUpperCase() !== "MERGE" || busy} data-testid="replace-merge-button">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Replace className="h-4 w-4 mr-1.5" />} Merge into {target?.name || "collector"}
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="convert" className="mt-3">
            <form onSubmit={convert} className="space-y-3" data-testid="replace-convert-form">
              <p className="text-xs text-muted-foreground">The placeholder record itself becomes the real person — same ID, so all {placeholder.receipts != null ? n(placeholder.receipts) : ""} receipts, assignments and targets stay exactly where they are.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Full name</Label><Input value={form.name} onChange={set("name")} required data-testid="convert-name-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={set("email")} required data-testid="convert-email-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Mobile (10 digits)</Label><Input value={form.mobile} onChange={set("mobile")} inputMode="numeric" pattern="\d{10}" data-testid="convert-mobile-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Employee ID</Label><Input value={form.employee_id} onChange={set("employee_id")} placeholder={`e.g. EMP-${String(Math.floor(Math.random() * 900) + 100)}`} data-testid="convert-employee-id-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Designation</Label><Input value={form.designation} onChange={set("designation")} data-testid="convert-designation-input" /></div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs">Reports to (Supervisor)</Label>
                  <Select value={form.reports_to_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, reports_to_id: v === "none" ? "" : v }))}>
                    <SelectTrigger className="h-10" data-testid="convert-reports-to-select"><SelectValue placeholder="Select supervisor" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="none">Keep current / not mapped</SelectItem>
                      {supervisors.map((s) => <SelectItem key={s.id} value={s.id}>{staffLabelText(s.name, s.employee_id)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs">Temporary password</Label>
                  <Input type="password" value={form.password} onChange={set("password")} required data-testid="convert-password-input" />
                  <p className="text-[11px] text-muted-foreground">{PASSWORD_RULES_HINT} They must change it at first sign-in.</p>
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="convert-cancel-button">Cancel</Button>
                <Button type="submit" disabled={busy} data-testid="convert-submit-button">
                  {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UserRoundCheck className="h-4 w-4 mr-1.5" />} Convert {placeholder.name} into {form.name.trim() || "this person"}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
